import { QueryCondition, QueryConditionItem } from './forge.types';

/**
 * Forge ⇄ blocks for conditions, IN THE ORDER WRITTEN (ADR 0011, 2026-10-06): a block's connector joins it to
 * everything above it. Forge's plan shape is unchanged — a list is an implicit AND, `{ or: [[…],[…]] }` an OR
 * of AND-branches, `{ and: [[…]] }` a bracket — so only the mapping between a flat run and that shape lives here,
 * shared by the reader (blockly-to-steps) and both compilers (forge-compiler, steps-to-blockly).
 */
export interface FoldLeaf { item: QueryConditionItem; connector: 'AND' | 'OR'; }

const isOr = (x: any): x is { or: QueryConditionItem[][] } => !!x && Array.isArray(x.or);

/** A bracket read back as `{ and: [[…]] }` after an OR is that OR's branch: `|| (C & D)` → [C, D]. */
function asBranch(item: QueryConditionItem): QueryCondition[] {
  const a: any = item;
  if (a && Array.isArray(a.and) && a.and.length === 1 && Array.isArray(a.and[0])) return a.and[0];
  return [item as QueryCondition];
}

/** Read a flat run: `&` appends to the AND list; `||` makes everything so far one branch of an OR. */
export function foldInOrder(leaves: FoldLeaf[]): QueryConditionItem[] {
  let acc: QueryConditionItem[] = [];
  leaves.forEach((l, i) => {
    if (i === 0 || l.connector !== 'OR') { acc.push(l.item); return; }
    const branch = asBranch(l.item);
    if (acc.length === 1 && isOr(acc[0])) acc = [{ or: [...acc[0].or, branch] } as any];
    else acc = [{ or: [acc as QueryCondition[], branch] } as any];
  });
  return acc;
}

/** How a compiler writes `{ or: branches }`: the first branch flat; each later branch joined with OR — one
 *  condition as itself, several as a group, because in order they must be bracketed. */
export interface OrPart { connector: 'AND' | 'OR'; items: QueryConditionItem[]; group: boolean; }

export function orBranchParts(branches: QueryConditionItem[][]): OrPart[] {
  const parts: OrPart[] = [];
  branches.forEach((branch, bi) => {
    if (bi === 0) { branch.forEach(it => parts.push({ connector: 'AND', items: [it], group: false })); return; }
    if (branch.length === 1) parts.push({ connector: 'OR', items: branch, group: false });
    else if (branch.length > 1) parts.push({ connector: 'OR', items: branch, group: true });
  });
  return parts;
}
